import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import {
  Plus,
  RefreshCw,
  Loader2,
  Search,
  Send,
  Pencil,
  Trash2,
  UserPlus,
  Settings2,
} from 'lucide-react'
import { Button } from '../lib/shadcn/button'
import { Input } from '../lib/shadcn/input'
import { Badge } from '../lib/shadcn/badge'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from '../lib/shadcn/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../lib/shadcn/table'
import { toast } from '../lib/shadcn/sonner'
import { useOnRestored, useUndoToast } from '../hooks/useUndoToast'
import { cn } from '../lib/shadcn/utils'
import {
  useGetLeads,
  useUpdateLead,
  useDeleteLead,
  useConvertLead,
  useGetCrmSettings,
} from '../hooks/backend/crm'
import LeadFormDialog, { STAGES, type Lead, type Stage } from './ui/LeadFormDialog'
import MessageDialog, { type MessageTarget } from './ui/MessageDialog'
import CrmSettingsDialog from './ui/CrmSettingsDialog'
import PriceSheetMenu from './ui/PriceSheetMenu'


const STAGE_STYLES: Record<Stage, string> = {
  New: 'bg-blue-100 text-blue-800 dark:bg-blue-950 dark:text-blue-200',
  Working: 'bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-200',
  Won: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-200',
  Lost: 'bg-rose-100 text-rose-800 dark:bg-rose-950 dark:text-rose-200',
}

export default function CrmPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const tabParam = searchParams.get('tab')
  const getLeads = useGetLeads()
  const updateLead = useUpdateLead()
  const deleteLead = useDeleteLead()
  const showUndo = useUndoToast()
  const convertLead = useConvertLead()
  const getCrmSettings = useGetCrmSettings()

  const [leadSearch, setLeadSearch] = useState('')
  const [stageFilter, setStageFilter] = useState<'All' | Stage>('All')

  const [formOpen, setFormOpen] = useState(false)
  const [editingLead, setEditingLead] = useState<Lead | null>(null)
  const [messageTarget, setMessageTarget] = useState<MessageTarget | null>(null)
  const [settingsOpen, setSettingsOpen] = useState(false)
  const [convertingId, setConvertingId] = useState<number | null>(null)

  const refresh = () => {
    getLeads.trigger({}, { skipCache: true })
  }
  useOnRestored(['lead'], refresh)

  // Customers and reorder reminders moved to the Customers page; keep old links working.
  useEffect(() => {
    if (tabParam === 'customers') navigate('/customers', { replace: true })
    else if (tabParam === 'reorders') navigate('/customers?tab=reorders', { replace: true })
  }, [tabParam, navigate])

  useEffect(() => {
    getLeads.trigger()
    getCrmSettings.trigger()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const leads = useMemo<Lead[]>(() => (getLeads.data as Lead[] | undefined) ?? [], [getLeads.data])
  const smsConfigured = Boolean((getCrmSettings.data as { sms_configured?: boolean } | undefined)?.sms_configured)

  const stageCounts = useMemo(() => {
    const counts: Record<string, number> = { New: 0, Working: 0, Won: 0, Lost: 0 }
    for (const l of leads) {
      if (counts[l.stage] !== undefined) counts[l.stage] = (counts[l.stage] ?? 0) + 1
    }
    return counts
  }, [leads])

  const filteredLeads = useMemo(() => {
    const q = leadSearch.trim().toLowerCase()
    return leads.filter(l => {
      if (stageFilter !== 'All' && l.stage !== stageFilter) return false
      if (!q) return true
      return (
        l.name.toLowerCase().includes(q) ||
        l.email.toLowerCase().includes(q) ||
        l.phone.toLowerCase().includes(q) ||
        l.notes.toLowerCase().includes(q)
      )
    })
  }, [leads, leadSearch, stageFilter])


  const handleStageChange = async (lead: Lead, stage: Stage) => {
    try {
      await updateLead.trigger({ id: lead.id, stage })
      refresh()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to update stage')
    }
  }

  const handleConvert = async (lead: Lead) => {
    setConvertingId(lead.id)
    try {
      const result = (await convertLead.trigger({ id: lead.id })) as { already_converted?: boolean; warnings?: string[] }
      toast.success(result.already_converted ? `${lead.name} is already a customer` : `${lead.name} converted to a customer`)
      for (const w of result.warnings ?? []) toast.warning(w, { duration: 10000 })
      refresh()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to convert lead')
    } finally {
      setConvertingId(null)
    }
  }

  // No "Are you sure?": the toast's Undo (and Settings > Recently deleted)
  // puts the lead back.
  const handleDelete = async (lead: Lead) => {
    try {
      const result = (await deleteLead.trigger({ id: lead.id })) as { trash_id?: number | null } | undefined
      showUndo(`${lead.name} deleted`, result?.trash_id)
      refresh()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to delete lead')
    }
  }

  return (
    <div className="max-w-6xl mx-auto p-4 sm:p-6 space-y-6">
      <div className="flex items-center gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Leads</h1>
          <p className="text-sm text-muted-foreground">
            Track and message leads. Customers and reorder reminders are on the Customers page.
          </p>
        </div>
        <div className="ml-auto flex items-center gap-2 flex-wrap">
          <PriceSheetMenu />
          <Button variant="outline" size="sm" onClick={() => setSettingsOpen(true)}>
            <Settings2 className="w-4 h-4" /> SMS Settings
          </Button>
          <Button variant="outline" size="sm" onClick={refresh} disabled={getLeads.loading}>
            <RefreshCw className={cn('w-4 h-4', getLeads.loading && 'animate-spin')} /> Refresh
          </Button>
        </div>
      </div>

      {/* ── Leads ── */}
      <div className="space-y-4">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {(STAGES as readonly Stage[]).map(stage => (
              <button
                key={stage}
                onClick={() => setStageFilter(prev => (prev === stage ? 'All' : stage))}
                className={cn(
                  'rounded-lg border p-3 text-left transition-colors',
                  stageFilter === stage ? 'border-primary bg-accent' : 'border-border hover:bg-accent/50'
                )}
              >
                <div className="text-xs font-medium text-muted-foreground">{stage}</div>
                <div className="text-2xl font-bold text-foreground tabular-nums">{stageCounts[stage] ?? 0}</div>
              </button>
            ))}
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                value={leadSearch}
                onChange={e => setLeadSearch(e.target.value)}
                placeholder="Search leads..."
                className="pl-8"
              />
            </div>
            {stageFilter !== 'All' && (
              <Button variant="ghost" size="sm" onClick={() => setStageFilter('All')}>
                Clear filter
              </Button>
            )}
            <Button
              size="sm"
              onClick={() => {
                setEditingLead(null)
                setFormOpen(true)
              }}
            >
              <Plus className="w-4 h-4" /> Add Lead
            </Button>
          </div>

          <div className="rounded-lg border border-border overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Contact</TableHead>
                  <TableHead>Stage</TableHead>
                  <TableHead>Notes</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {getLeads.loading && leads.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-10 text-muted-foreground">
                      <Loader2 className="w-5 h-5 animate-spin inline" />
                    </TableCell>
                  </TableRow>
                ) : filteredLeads.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-10 text-muted-foreground">
                      No leads found.
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredLeads.map(lead => (
                    <TableRow key={lead.id}>
                      <TableCell>
                        <button
                          className="font-medium text-primary hover:underline text-left"
                          onClick={() => navigate(`/crm/leads/${lead.id}`)}
                        >
                          {lead.name}
                        </button>
                        {lead.converted_customer_id && (
                          <Badge variant="outline" className="mt-1 text-xs">Customer</Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="text-sm text-foreground">{lead.email || '—'}</div>
                        <div className="text-xs text-muted-foreground">{lead.phone || '—'}</div>
                      </TableCell>
                      <TableCell>
                        <Select value={lead.stage} onValueChange={v => handleStageChange(lead, v as Stage)}>
                          <SelectTrigger className="h-8 w-[120px] border-none px-2 shadow-none focus:ring-0">
                            <Badge className={cn('font-medium', STAGE_STYLES[lead.stage as Stage] ?? '')} variant="secondary">
                              {lead.stage}
                            </Badge>
                          </SelectTrigger>
                          <SelectContent>
                            {STAGES.map(s => (
                              <SelectItem key={s} value={s}>{s}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell className="max-w-[240px]">
                        <p className="text-sm text-muted-foreground truncate">{lead.notes || '—'}</p>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            aria-label="Message lead"
                            onClick={() =>
                              setMessageTarget({ leadId: lead.id, name: lead.name, email: lead.email, phone: lead.phone })
                            }
                          >
                            <Send className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            aria-label="Edit lead"
                            onClick={() => {
                              setEditingLead(lead)
                              setFormOpen(true)
                            }}
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                          {!lead.converted_customer_id && (
                            <Button
                              variant="ghost"
                              size="icon"
                              aria-label="Convert to customer"
                              title="Convert to customer"
                              disabled={convertingId === lead.id}
                              onClick={() => handleConvert(lead)}
                            >
                              {convertingId === lead.id ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <UserPlus className="w-4 h-4" />
                              )}
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="icon"
                            aria-label="Delete lead"
                            className="text-muted-foreground hover:text-destructive"
                            onClick={() => void handleDelete(lead)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
      </div>

      <LeadFormDialog open={formOpen} lead={editingLead} onOpenChange={setFormOpen} onSaved={refresh} />
      <MessageDialog target={messageTarget} smsConfigured={smsConfigured} onOpenChange={o => {
          if (!o) {
            setMessageTarget(null)
          }
        }} />
      <CrmSettingsDialog open={settingsOpen} onOpenChange={setSettingsOpen} onSaved={() => getCrmSettings.trigger({}, { skipCache: true })} />

    </div>
  )
}
